import { TypedEventEmitter } from '../utils/TypedEventEmitter';
import { NOOP_LOGGER, writeLog, type StructuredLogger } from '../webrtc/logger';
import { normalizeStatsReport, type StatsCounterBaseline } from './normalizeStats';
import {
  StatsCollectorError,
  type ConnectionMetrics,
  type StatsCollectorEvents,
  type StatsCollectorState,
} from './types';

const DEFAULT_INTERVAL_MS = 1_000;

export interface StatsCollectorOptions {
  readonly intervalMs?: number;
  readonly now?: () => number;
  readonly logger?: StructuredLogger;
}

export class StatsCollector extends TypedEventEmitter<StatsCollectorEvents> {
  private readonly intervalMs: number;
  private readonly now: () => number;
  private readonly logger: StructuredLogger;
  private baseline: StatsCounterBaseline | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private inFlight: Promise<ConnectionMetrics> | null = null;
  private runGeneration = 0;
  private latestMetrics: ConnectionMetrics | null = null;
  private _state: StatsCollectorState = 'idle';

  constructor(
    private readonly connection: RTCPeerConnection,
    options: StatsCollectorOptions = {},
  ) {
    super();
    const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
    if (!Number.isFinite(intervalMs) || intervalMs <= 0) {
      throw new StatsCollectorError('Stats collection interval must be positive.');
    }
    this.intervalMs = intervalMs;
    this.now = options.now ?? Date.now;
    this.logger = options.logger ?? NOOP_LOGGER;
  }

  get state(): StatsCollectorState {
    return this._state;
  }

  get latest(): ConnectionMetrics | null {
    return this.latestMetrics;
  }

  subscribe(listener: (metrics: ConnectionMetrics) => void): () => void {
    if (this.latestMetrics !== null) {
      listener(this.latestMetrics);
    }
    return this.on('metrics', ({ metrics }) => listener(metrics));
  }

  start(): void {
    if (this._state === 'destroyed') {
      throw new StatsCollectorError('Cannot restart a destroyed stats collector.');
    }
    if (this._state === 'running') {
      return;
    }

    this.runGeneration += 1;
    const generation = this.runGeneration;
    this.setState('running');
    this.collectScheduled(generation);
    this.timer = setInterval(() => this.collectScheduled(generation), this.intervalMs);
  }

  stop(): void {
    if (this._state === 'destroyed' || this._state === 'stopped') {
      return;
    }
    this.runGeneration += 1;
    this.clearTimer();
    this.inFlight = null;
    this.baseline = null;
    this.setState('stopped');
  }

  collectNow(): Promise<ConnectionMetrics> {
    if (this._state === 'destroyed') {
      return Promise.reject(new StatsCollectorError('Stats collector has been destroyed.'));
    }
    return this.collect(null);
  }

  destroy(): void {
    if (this._state === 'destroyed') {
      return;
    }
    this.runGeneration += 1;
    this.clearTimer();
    this.inFlight = null;
    this.baseline = null;
    this.latestMetrics = null;
    this.setState('destroyed');
    this.clearListeners();
  }

  private collectScheduled(generation: number): void {
    void this.collect(generation).catch(() => {
      // Errors are emitted by collect(); the interval remains alive for transient failures.
    });
  }

  private collect(generation: number | null): Promise<ConnectionMetrics> {
    if (this.inFlight !== null) {
      return this.inFlight;
    }

    const operation = this.connection
      .getStats()
      .then((report) => {
        const collectedAt = this.now();
        const normalized = normalizeStatsReport(report, collectedAt, this.baseline);
        const shouldPublish =
          generation === null || (this._state === 'running' && generation === this.runGeneration);
        if (shouldPublish) {
          this.baseline = normalized.baseline;
          this.latestMetrics = normalized.metrics;
          this.emit('metrics', { metrics: normalized.metrics });
          writeLog(this.logger, 'debug', 'stats', 'snapshot.collected', {
            connectionPath: normalized.metrics.connectionPath,
            hasVideo: normalized.metrics.framesEncoded !== null,
          });
        }
        return normalized.metrics;
      })
      .catch((cause: unknown) => {
        const error = new StatsCollectorError('Could not collect WebRTC statistics.', { cause });
        const shouldPublish =
          generation === null || (this._state === 'running' && generation === this.runGeneration);
        if (shouldPublish) {
          writeLog(this.logger, 'warn', 'stats', 'collection.failed', {
            state: this._state,
          });
          this.emit('error', { error });
        }
        throw error;
      })
      .finally(() => {
        if (this.inFlight === operation) {
          this.inFlight = null;
        }
      });
    this.inFlight = operation;
    return operation;
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private setState(state: StatsCollectorState): void {
    if (this._state === state) {
      return;
    }
    const previous = this._state;
    this._state = state;
    this.emit('statechange', { previous, current: state });
  }
}
